import consts from "./consts";
import utils from "./utils";
import * as PIXI from "pixi.js";
import PixiFps from "pixi-fps";
import { Howl } from "howler";
import auth from "./services/auth";
import "./style.scss";
import GameScene from "./scenes/Game";
import PreloadScene from "./scenes/Preload";

PIXI.settings.RESOLUTION = window.devicePixelRatio || 1;
PIXI.settings.FILTER_RESOLUTION = utils.isMobile ? 2 : 1;

async function applyExternalTokensIfAny() {
  const accessToken = getUrlsAccessToken();
  const refreshToken = getUrlsRefreshToken();
  if (!accessToken) return false;
  try {
    window.localStorage.setItem("accessToken", accessToken);
    if (refreshToken) window.localStorage.setItem("refreshToken", refreshToken);
    console.info("[TOKEN INJECT] tokens applied to localStorage");

    return true;
  } catch (err) {
    console.error("[TOKEN INJECT] failed to apply tokens", err);
    return false;
  }
}

// Çağır: loader çalışmadan önce token'ı uygula
(async () => {
  await applyExternalTokensIfAny();
})();

window.PIXI = PIXI;

const app = new PIXI.Application({
  width: consts.game.width,
  height: consts.game.height,
  antialias: true,
  transparent: true,
  resizeTo: window,
  autoDensity: true,
  resolution: window.devicePixelRatio || 1,
});

app.renderer.resize(window.innerWidth, window.innerHeight);
app.renderer.runners.resize.add({
  resize: onResize,
});

const preloadScene = new PreloadScene();
app.stage.addChild(preloadScene);

consts.assets.symbol.animatedSprites.forEach((sprite) => {
  app.loader.add(
    sprite.name,
    utils.assetUrl(sprite.path, consts.assets.symbol.root)
  );
});
consts.assets.general.forEach((image) => {
  app.loader.add(image.name, utils.assetUrl(image.path));
});

async function loadSounds() {
  return await new Promise((resolve) => {
    let loadedCount = 0;
    let soundInstances = {};
    consts.assets.sounds.forEach((sound) => {
      const soundOptions = {
        src: `${consts.assets.root}/${sound.url}`,
        format: "mp3",
      };

      if (sound.sprite) {
        const tracks = {};
        sound.tracks.forEach((track) => {
          tracks[track.name] = track.fromTo.map((s) => s * 1000);
        });
        soundOptions.sprite = tracks;
      }
      soundInstances[sound.name] = new Howl(soundOptions);
      soundInstances[sound.name].once("load", () => {
        loadedCount++;
        if (consts.assets.sounds.length === loadedCount)
          resolve(soundInstances);
      });
    });
  });
}

function getUrlsAccessToken() {
  let params = new URLSearchParams(window.location.search);
  if (params.get("access_token")) return params.get("access_token");
  return false;
}

function getUrlsRefreshToken() {
  let params = new URLSearchParams(window.location.search);
  if (params.get("refresh_token")) return params.get("refresh_token");
  return false;
}

async function authAndLoad(loader, resources) {
  const variationConfig = await auth.getSlotVariationConfig();
  const user = await auth.getUserDetails();
  const soundInstances = await loadSounds();
  await preloadScene.finish();
  app.stage.removeChild(preloadScene);
  const gameScene = new GameScene({
    variationConfig,
    renderer: app.renderer,
    loader,
    resources,
    sounds: soundInstances,
    user,
  });
  app.stage.addChild(gameScene);

  if (process.env.FPS_METER && process.env.NODE_ENV === "development") {
    console.info("DEVELOPMENT MODE", true);
    app.stage.addChild(new PixiFps());
  }
}

// ----------------
// Loader progress
// ----------------
app.loader.onLoad.add(async () => {
  const assetsCount =
    consts.assets.symbol.animatedSprites.length + consts.assets.general.length;

  // ✅ Fazladan çağrılmayı engelle
  if (preloadScene.loadedAssetsCount < assetsCount) {
    await preloadScene.increaseLoadingBarProgress(assetsCount);
  }
});

// ----------------
// Loader complete
// ----------------
app.loader.load(async function (loader, resources) {
  // 1) URL paramları ile token geliyorsa tercih et
  if (getUrlsAccessToken() && getUrlsRefreshToken()) {
    console.log("loader bitti");
    try {
      await auth.init(getUrlsAccessToken(), getUrlsRefreshToken());
      await authAndLoad(loader, resources);
      return;
    } catch (err) {
      console.warn("[LOADER] authService.init via URL tokens failed", err);
      // fallthrough -> localStorage veya fallback'a bak
    }
  }

  // 2) localStorage veya applyExternalTokensIfAny() tarafından ayarlanmış token var mı?
  const storedAccess = window.localStorage.getItem("accessToken");
  if (storedAccess) {
    try {
      // init etmeye çalış (refresh token doğrulaması yapar)
      await auth.init(
        storedAccess,
        window.localStorage.getItem("refreshToken")
      );
      doMagic();

      await authAndLoad(loader, resources);
      return;
    } catch (err) {
      console.warn(
        "[LOADER] authService.init via localStorage tokens failed",
        err
      );
      // fallthrough -> fallback mock
    }
  }

  // 3) Hiç token yoksa (local dev): fallback — direkt aç veya mock kullan
  console.log(
    "[LOADER] No tokens found — using local fallback (opening game with mocks)"
  );
  try {
    // opsiyon: authAndLoad içinde zaten mock yapı yoksa, buraya da try/catch içeren bir mock çağırabilirsin
    await preloadScene.finish();
    await authAndLoad(loader, resources);
  } catch (err) {
    console.error("[LOADER] authAndLoad failed in fallback", err);
  }
});

function onResize(width, height) {
  const _preloadScene = app.stage.getChildByName("PRELOAD");
  const _gameScene = app.stage.getChildByName("GAME");
  if (_preloadScene) _preloadScene.onResize(width, height);
  if (_gameScene) _gameScene.onResize(width, height);
}

document.addEventListener("DOMContentLoaded", () => {
  document.body.appendChild(app.view);
  window.WebFontConfig = {
    google: {
      families: ["Shojumaru", "Arvo:700italic", "Podkova:700"],
    },
  };
  (function () {
    const wf = document.createElement("script");
    wf.src = `${
      document.location.protocol === "https:" ? "https" : "http"
    }://ajax.googleapis.com/ajax/libs/webfont/1/webfont.js`;
    wf.type = "text/javascript";
    wf.async = "true";
    const s = document.getElementsByTagName("script")[0];
    s.parentNode.insertBefore(wf, s);
  })();
});

function doMagic() {
  var el = document.createElement("p");
  el.style.fontFamily = "Reggae One";
  el.style.fontSize = "0px";
  el.style.visibility = "hidden";
  el.innerHTML = ".";

  document.body.appendChild(el);
}
